import { computePosition, flip, offset, shift } from "@floating-ui/dom";
import { Extension, type Editor, type Range } from "@tiptap/core";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TableKit } from "@tiptap/extension-table";
import { Plugin, PluginKey, Selection, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Placeholder } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import { EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Suggestion, { type SuggestionKeyDownProps, type SuggestionProps } from "@tiptap/suggestion";
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Quote,
  SquareCode,
  Table as TableIcon,
  Strikethrough,
  Text,
  type LucideIcon,
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { plainTextToMarkdown, readableMarkdown } from "../markdown-text";
import { coverDisplay, splitCover } from "../lesson-doc";
import { PipeSafeTable } from "../doc-table";

/**
 * A Notion-style document editor that reads and writes MARKDOWN.
 *
 * ⚠️ Markdown, not editor JSON or HTML, is what gets saved. The knowledge base's
 *    `full_text` is read verbatim by the AI Analyst and re-embedded by a database
 *    trigger on every change, so it must stay plain, readable text.
 *
 * The same component renders the read-only view, so a document looks identical
 * whether you are reading it or editing it.
 */

type Command = {
  title: string;
  hint: string;
  icon: LucideIcon;
  keywords: string;
  run: (editor: Editor, range: Range) => void;
};

const COMMANDS: Command[] = [
  { title: "Text", hint: "Plain paragraph", icon: Text, keywords: "paragraph p", run: (e, r) => e.chain().focus().deleteRange(r).setParagraph().run() },
  { title: "Heading 1", hint: "Big section heading", icon: Heading1, keywords: "h1 title", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 1 }).run() },
  { title: "Heading 2", hint: "Medium heading", icon: Heading2, keywords: "h2 subtitle", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 2 }).run() },
  { title: "Heading 3", hint: "Small heading", icon: Heading3, keywords: "h3", run: (e, r) => e.chain().focus().deleteRange(r).setHeading({ level: 3 }).run() },
  { title: "Bulleted list", hint: "Simple list", icon: List, keywords: "ul bullet unordered", run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { title: "Numbered list", hint: "Ordered steps", icon: ListOrdered, keywords: "ol ordered number", run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { title: "To-do list", hint: "Checkboxes", icon: ListChecks, keywords: "task todo check", run: (e, r) => e.chain().focus().deleteRange(r).toggleTaskList().run() },
  { title: "Quote", hint: "Call out a passage", icon: Quote, keywords: "blockquote", run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { title: "Code block", hint: "Monospaced block", icon: SquareCode, keywords: "code pre", run: (e, r) => e.chain().focus().deleteRange(r).toggleCodeBlock().run() },
  { title: "Table", hint: "Rows and columns", icon: TableIcon, keywords: "grid columns rows", run: (e, r) => e.chain().focus().deleteRange(r).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { title: "Divider", hint: "Horizontal rule", icon: Minus, keywords: "hr line separator", run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
];

type MenuHandle = { onKeyDown: (p: SuggestionKeyDownProps) => boolean };

const SlashMenu = forwardRef<MenuHandle, SuggestionProps<Command>>(function SlashMenu({ items, command }, ref) {
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [items]);
  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (!items.length) return false;
      if (event.key === "ArrowDown") { setActive((a) => (a + 1) % items.length); return true; }
      if (event.key === "ArrowUp") { setActive((a) => (a - 1 + items.length) % items.length); return true; }
      if (event.key === "Enter") { command(items[active]); return true; }
      return false;
    },
  }));
  if (!items.length) return <div className="doc-slash"><p className="doc-slash-empty">No matching blocks</p></div>;
  return (
    <div className="doc-slash" role="listbox" aria-label="Insert block">
      <p className="doc-slash-label">Basic blocks</p>
      {items.map((item, i) => (
        <button
          key={item.title}
          type="button"
          role="option"
          aria-selected={i === active}
          className={i === active ? "active" : ""}
          onMouseEnter={() => setActive(i)}
          onMouseDown={(e) => { e.preventDefault(); command(item); }}
        >
          <span className="doc-slash-icon"><item.icon aria-hidden="true" /></span>
          <span><strong>{item.title}</strong><small>{item.hint}</small></span>
        </button>
      ))}
    </div>
  );
});

const SlashCommand = Extension.create({
  name: "slashCommand",
  addProseMirrorPlugins() {
    return [
      Suggestion<Command>({
        editor: this.editor,
        char: "/",
        startOfLine: false,
        allowSpaces: false,
        items: ({ query }) => {
          const q = query.toLowerCase();
          return COMMANDS.filter((c) => `${c.title} ${c.keywords}`.toLowerCase().includes(q));
        },
        command: ({ editor, range, props }) => props.run(editor, range),
        render: () => {
          let renderer: ReactRenderer<MenuHandle, SuggestionProps<Command>> | null = null;
          const place = (props: SuggestionProps<Command>) => {
            const el = renderer?.element as HTMLElement | undefined;
            const rect = props.clientRect?.();
            if (!el || !rect) return;
            const virtual = { getBoundingClientRect: () => rect };
            void computePosition(virtual, el, { placement: "bottom-start", strategy: "fixed", middleware: [offset(6), flip(), shift({ padding: 8 })] })
              .then(({ x, y }) => Object.assign(el.style, { left: `${x}px`, top: `${y}px` }));
          };
          return {
            onStart: (props) => {
              renderer = new ReactRenderer(SlashMenu, { props, editor: props.editor });
              const el = renderer.element as HTMLElement;
              el.style.position = "fixed";
              el.style.zIndex = "200";
              document.body.appendChild(el);
              place(props);
            },
            onUpdate: (props) => { renderer?.updateProps(props); place(props); },
            onKeyDown: (props) => {
              if (props.event.key === "Escape") { renderer?.element.remove(); return true; }
              return renderer?.ref?.onKeyDown(props) ?? false;
            },
            onExit: () => { renderer?.element.remove(); renderer?.destroy(); renderer = null; },
          };
        },
      }),
    ];
  },
});

/**
 * Short "·"-separated lines ("KNOWLEDGE BASE · KB-001 · READ FIRST", "Category:
 * SOPs · June 2026") are a document's cover metadata: they get an `is-meta`
 * class and are drawn small and quiet. Display only: the text is untouched.
 * Headings are always drawn as headings, in reading and editing alike; ALL-CAPS
 * ones reach the editor already in title case (calmHeadings).
 *
 * The cover's label and category lines are shown the way the reader shows them
 * ("Read first", "Category: Agency Foundation"; lesson-doc coverDisplay) until the
 * caret enters one, which then shows its full text to edit. Display only: the
 * saved Markdown keeps every word.
 */
const metaKey = new PluginKey<DecorationSet>("metaLines");
type PMNode = import("@tiptap/pm/model").Node;

/** The document's opening blocks, as coverDisplay reads them: short paragraphs and headings, up to the body. */
function openingLines(doc: PMNode) {
  const lines: { from: number; to: number; text: string; paragraph: boolean; level: number }[] = [];
  for (let i = 0, pos = 0; i < doc.childCount && lines.length < 10; i++) {
    const node = doc.child(i);
    const text = node.textContent.trim();
    if (!["paragraph", "heading"].includes(node.type.name) || text.length > 160) break;
    const paragraph = node.type.name === "paragraph";
    lines.push({ from: pos, to: pos + node.nodeSize, text, paragraph, level: paragraph ? 0 : Number(node.attrs.level) || 1 });
    pos += node.nodeSize;
  }
  return lines;
}

export type CoverRole = "label" | "title" | "line" | "meta";

/**
 * Which opening blocks the reader draws in its cover block (lesson-doc splitCover), and as
 * what: the label, the cover title, the lines under it, the category line. The editor gives
 * them the same look, so pressing Edit does not reflow the top of the document. Null for a
 * block the reader leaves in the body. Every block stays editable; a line the reader hides
 * (one that repeats the page title) still shows here, as a cover line.
 */
export function coverRoles(lines: { text: string; level: number }[], title?: string): (CoverRole | null)[] {
  const none = lines.map(() => null);
  // The same Markdown the reader splits, one block per line; "# end" stands for the body.
  const blocks: { text: string; at: number }[] = [];
  lines.forEach((line, at) => { if (line.text) blocks.push({ text: line.level ? `${"#".repeat(line.level)} ${line.text}` : line.text, at }); });
  const md = [...blocks.map((b) => b.text), "# end"].join("\n\n");
  const { cover, body } = splitCover(md, title);
  if (!cover) return none;
  const taken = blocks.length + 1 - body.split(/\n{2,}/).length;
  if (taken < 1) return none;
  const last = blocks[taken - 1].at;
  const plain = (text: string) => text.replace(/^#+ /, "");
  return lines.map((_, at) => {
    if (at > last) return null;
    const index = blocks.findIndex((b) => b.at === at);
    if (index < 0) return "line";
    const text = plain(blocks[index].text);
    const letters = text.replace(/[^A-Za-z]/g, "");
    if (index === 0 && letters && letters === letters.toUpperCase()) return "label";
    if (/^(category|updated|owner|version)\s*:/i.test(text)) return "meta";
    return cover.title && text === cover.title ? "title" : "line";
  });
}

const MetaLines = Extension.create<{ title?: string }>({
  name: "metaLines",
  addOptions() {
    return { title: undefined };
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    const title = this.options.title;
    // `selection` is null on first paint: the editor's default caret sits in the first line,
    // which would open the label before anyone has clicked.
    const build = (doc: PMNode, selection: Selection | null) => {
      const decos: Decoration[] = [];
      const marked = new Map<number, Decoration>();
      const opening = openingLines(doc);
      const firstText = opening.findIndex((l) => l.text);
      // While editing, the cover lines keep the reader's cover look (the reading view draws
      // its own cover block above a body that no longer has these lines).
      const roles = editor.options.editable ? coverRoles(opening, title) : opening.map(() => null);
      const first = roles.findIndex(Boolean);
      const last = roles.length - 1 - [...roles].reverse().findIndex(Boolean);
      coverDisplay(opening.map((l) => l.text)).forEach((shown, i) => {
        const line = opening[i];
        const role = roles[i];
        // The caret is in this line while editing: show what is really there.
        const caretIn = !!selection && editor.isEditable && selection.from < line.to && selection.to > line.from;
        const fold = !!shown && line.paragraph && !caretIn;
        const names = new Set<string>();
        if (fold) ["is-meta", "is-cover-folded", ...(role ? [] : [i === firstText ? "cover-label" : "cover-meta"])].forEach((n) => names.add(n));
        if (role) {
          ["cover-block", `cover-${role}`].forEach((n) => names.add(n));
          if (role === "label" || role === "meta") names.add("is-meta");
          if (i === first) names.add("cover-first");
          if (i === last) names.add("cover-end");
        }
        if (!names.size) return;
        marked.set(line.from, Decoration.node(line.from, line.to, { class: [...names].join(" "), ...(fold ? { "data-cover": shown || "" } : {}) }, fold ? { folded: true } : {}));
      });
      doc.descendants((node, pos) => {
        const mark = marked.get(pos);
        if (mark) { decos.push(mark); return false; }
        if (node.type.name !== "paragraph") return;
        if (node.textContent.length <= 160 && /\S\s+·\s+\S/.test(node.textContent)) decos.push(Decoration.node(pos, pos + node.nodeSize, { class: "is-meta" }));
        return false;
      });
      return DecorationSet.create(doc, decos);
    };
    return [new Plugin({
      key: metaKey,
      state: { init: (_, { doc }) => build(doc, null), apply: (tr, old) => (tr.docChanged || tr.selectionSet ? build(tr.doc, tr.selection) : old) },
      props: { decorations(state) { return metaKey.getState(state); } },
    })];
  },
});

export type DocEditorHandle = { getMarkdown: () => string; setMarkdown: (md: string) => void; focus: () => void };

/** Below the app's sticky bars (top bar, section tabs, document toolbar): where the screen's first readable line is. */
const VIEW_TOP = 160;

function ToolbarButton({ label, active, onClick, children }: { label: string; active?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className={active ? "active" : ""} aria-label={label} title={label} aria-pressed={!!active} onMouseDown={(e) => { e.preventDefault(); onClick(); }}>
      {children}
    </button>
  );
}

const DocEditor = forwardRef<DocEditorHandle, {
  markdown: string;
  editable: boolean;
  placeholder?: string;
  /** The page title above the document: a cover line that repeats it is told apart the way the reader does. */
  title?: string;
  onDirty?: () => void;
}>(function DocEditor({ markdown, editable, placeholder = "Type '/' for blocks, or just start writing…", title, onDirty }, ref) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: { openOnClick: false, autolink: true } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      // Tables must be understood by the editor or a table in the Markdown
      // would be dropped on save. Not resizable: widths are not Markdown.
      TableKit.configure({ table: false }),
      PipeSafeTable.configure({ resizable: false }),
      MetaLines.configure({ title }),
      Placeholder.configure({ placeholder }),
      Markdown,
      SlashCommand,
    ],
    content: plainTextToMarkdown(markdown),
    contentType: "markdown",
    editable,
    immediatelyRender: true,
    editorProps: { attributes: { class: "doc-prose", spellcheck: "true" } },
    // ⚠️ Only a real content change is an edit. Tiptap also emits "update" for
    //    setEditable() and other non-document events; counting those marked every
    //    freshly opened document as having unsaved changes.
    onUpdate: ({ transaction }) => { if (transaction.docChanged) onDirty?.(); },
  });

  // `false`: switching read/edit mode is not a content change, so emit no update.
  useEffect(() => { editor?.setEditable(editable, false); }, [editor, editable]);

  useImperativeHandle(ref, () => ({
    getMarkdown: () => readableMarkdown(editor?.getMarkdown() ?? ""),
    setMarkdown: (md: string) => { editor?.commands.setContent(plainTextToMarkdown(md), { contentType: "markdown", emitUpdate: true }); },
    // The caret goes to the first line on screen, and the page does not move: focusing the
    // end of a long document used to throw the reader to its last paragraph.
    focus: () => {
      if (!editor || editor.isDestroyed) return;
      const box = editor.view.dom.getBoundingClientRect();
      const top = Math.min(Math.max(box.top + 12, VIEW_TOP), box.bottom - 4);
      const at = top > 0 && top < window.innerHeight ? editor.view.posAtCoords({ left: box.left + 24, top }) : null;
      // The nearest place text can go (inside a paragraph or a table cell, not between blocks).
      const { doc } = editor.state;
      let near = at ? Selection.near(doc.resolve(at.pos)) : Selection.atStart(doc);
      // A folded cover line ("Read first") opens when the caret enters it, so the caret starts just after it.
      const fold = metaKey.getState(editor.state)?.find(undefined, undefined, (spec) => !!spec?.folded).find((d) => near.from > d.from && near.from < d.to);
      if (fold) near = Selection.near(doc.resolve(Math.min(fold.to, doc.content.size)), 1);
      editor.commands.focus(near instanceof TextSelection ? near.from : "start", { scrollIntoView: false });
    },
  }), [editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address", previous || "https://");
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  return (
    <div className={editable ? "doc-editor editing" : "doc-editor"}>
      {editable && (
        <BubbleMenu editor={editor} className="doc-bubble" options={{ placement: "top", offset: 8 }}>
          <ToolbarButton label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold aria-hidden="true" /></ToolbarButton>
          <ToolbarButton label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic aria-hidden="true" /></ToolbarButton>
          <ToolbarButton label="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough aria-hidden="true" /></ToolbarButton>
          <ToolbarButton label="Inline code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code aria-hidden="true" /></ToolbarButton>
          <span className="doc-bubble-sep" aria-hidden="true" />
          <ToolbarButton label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 aria-hidden="true" /></ToolbarButton>
          <ToolbarButton label="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List aria-hidden="true" /></ToolbarButton>
          <ToolbarButton label="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon aria-hidden="true" /></ToolbarButton>
        </BubbleMenu>
      )}
      <EditorContent editor={editor} />
    </div>
  );
});

export default DocEditor;
